/**
 * The page of one hypothesis (PRD-0075 R-E1…R-E4): the claim, its focus entities (each opens in
 * the graph), the strip of its assessments, what it rests on (research and evidence, passed in
 * as children), every assessment newest first and, for a collaborator on an open hypothesis, the
 * form that adds one. Presentational: the feature reads the hypothesis and passes the rest in.
 */
import {
  CircleSlash,
  Lightbulb,
  ListChecks,
  Lock,
  LockOpen,
  Quote,
  RefreshCw,
  Target,
  TrendingUp,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  AnalyticHypothesisDetail,
  EvidenceRefOut,
  FocusEntity,
} from '@/types/analytic-hypothesis'
import { formatDate } from '@/utils/date'
import { AssessmentList } from './AssessmentList'
import { AssessmentStrip } from './AssessmentStrip'
import { stripEnd, VerdictBadge } from './HypothesisCard'
import { HypothesisSection } from './HypothesisSection'

const COPY = 'analyticHypotheses'

/** Close or reopen, for a collaborator. */
export interface HypothesisStatusAction {
  onToggle: () => void
  pending: boolean
}

export interface HypothesisLinks {
  /** The graph, focused on one node. */
  entity: (nodeId: string) => string
  /** Where a cited item opens; null when it has no page of its own. */
  evidence: (ref: EvidenceRefOut) => string | null
}

export interface HypothesisDetailViewProps {
  hypothesis: AnalyticHypothesisDetail
  links: HypothesisLinks
  /** Null for a reader. */
  statusAction: HypothesisStatusAction | null
  /** The add-assessment form; null for a reader or a closed hypothesis. */
  form: ReactNode
  /** What the hypothesis rests on (research runs, evidence), between the strip and the form. */
  children?: ReactNode
}

function FocusChip({ entity, href }: { entity: FocusEntity; href: string | null }) {
  const { t } = useTranslation()
  if (!href) {
    return (
      <Badge variant="muted" className="h-6" title={t(`${COPY}.detail.notInGraph`)}>
        <CircleSlash aria-hidden />
        {entity.name ?? t(`${COPY}.detail.notInGraph`)}
      </Badge>
    )
  }
  const name = entity.name ?? entity.uid
  return (
    <Badge variant="outline" className="h-6" asChild>
      <Link to={href} aria-label={t(`${COPY}.detail.openInGraph`, { name })}>
        {name}
        {entity.label && <span className="text-muted-foreground">{entity.label}</span>}
      </Link>
    </Badge>
  )
}

function Header({
  hypothesis,
  statusAction,
}: Pick<HypothesisDetailViewProps, 'hypothesis' | 'statusAction'>) {
  const { t, i18n } = useTranslation()
  const closed = hypothesis.status === 'closed'
  return (
    <header className="flex flex-col gap-2">
      <div className="flex flex-wrap items-start gap-3">
        <Lightbulb aria-hidden className="mt-1 size-5 shrink-0 text-muted-foreground" />
        <h1 className="min-w-0 flex-1 text-xl font-semibold tracking-tight text-foreground">
          {hypothesis.title}
        </h1>
        {statusAction && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={statusAction.pending}
            onClick={statusAction.onToggle}
          >
            {closed ? <LockOpen aria-hidden /> : <Lock aria-hidden />}
            {t(closed ? `${COPY}.detail.reopen` : `${COPY}.detail.close`)}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
        <VerdictBadge point={hypothesis.latest} />
        <Badge variant={closed ? 'muted' : 'info'}>
          {t(`${COPY}.status.${hypothesis.status}`)}
        </Badge>
        <span>
          {t(`${COPY}.detail.createdBy`, {
            name: hypothesis.created_by_name,
            date: formatDate(hypothesis.created_at, i18n.language),
          })}
        </span>
        {hypothesis.closed_at && (
          <span>
            {t(`${COPY}.detail.closedOn`, {
              date: formatDate(hypothesis.closed_at, i18n.language),
            })}
          </span>
        )}
      </div>
    </header>
  )
}

export function HypothesisDetailView({
  hypothesis,
  links,
  statusAction,
  form,
  children,
}: HypothesisDetailViewProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4">
      <Header hypothesis={hypothesis} statusAction={statusAction} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <HypothesisSection title={t(`${COPY}.detail.claim`)} icon={<Quote className="size-4" />}>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {hypothesis.claim}
            </p>
          </HypothesisSection>
          <HypothesisSection
            title={t(`${COPY}.detail.evolution`)}
            icon={<TrendingUp className="size-4" />}
          >
            <AssessmentStrip points={hypothesis.strip} end={stripEnd(hypothesis)} size="full" />
          </HypothesisSection>
          {form}
          {hypothesis.status === 'closed' && statusAction && (
            <p className="text-xs text-muted-foreground">{t(`${COPY}.detail.closedHint`)}</p>
          )}
          <HypothesisSection
            title={t(`${COPY}.detail.assessments`)}
            icon={<ListChecks className="size-4" />}
          >
            <AssessmentList assessments={hypothesis.assessments} evidenceHref={links.evidence} />
          </HypothesisSection>
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <HypothesisSection title={t(`${COPY}.detail.focus`)} icon={<Target className="size-4" />}>
            {hypothesis.focus_entities.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t(`${COPY}.detail.noFocus`)}</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {hypothesis.focus_entities.map(entity => (
                  <li key={entity.uid}>
                    <FocusChip
                      entity={entity}
                      href={entity.node_id ? links.entity(entity.node_id) : null}
                    />
                  </li>
                ))}
              </ul>
            )}
          </HypothesisSection>
          {children}
        </div>
      </div>
    </div>
  )
}

/** The page while its hypothesis is read. */
export function HypothesisDetailLoading() {
  const { t } = useTranslation()
  return (
    <div role="status" className="flex flex-col gap-4">
      <span className="sr-only">{t(`${COPY}.detail.loading`)}</span>
      <Skeleton aria-hidden className="h-8 w-2/3" />
      <Skeleton aria-hidden className="h-16 w-full" />
      <Skeleton aria-hidden className="h-24 w-full" />
    </div>
  )
}

/** The page when its hypothesis could not be read. */
export function HypothesisDetailFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
      <p className="text-destructive">{t(`${COPY}.detail.loadFailed`)}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw aria-hidden />
        {t(`${COPY}.detail.retry`)}
      </Button>
    </div>
  )
}
